import { useEffect, useRef } from "react";
import { TTSWaveformColor } from "shared/constants";


/*

Visualizes real-time audio frequency data from an AnalyserNode on a canvas using vertical bars mirrored around the center line.

The AnalyserNode sits in the AudioContext audio graph, observing audio as it flows from scheduled sources to the speakers without
modifying it. 

In short : It contains data like frequency data, time data, etc. used for the wave form visualisation

*/


interface AudioWaveformProps {
  analyser: AnalyserNode | null;
}

export default function AudioWaveform({ analyser }: AudioWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafIdRef = useRef<number>();

  const drawStraightLine = (
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement
  ) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const centerY = canvas.height / 2;
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(canvas.width, centerY);
    ctx.strokeStyle = TTSWaveformColor;
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    drawStraightLine(ctx, canvas);
    if (!analyser) return;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    const draw = () => {
      analyser.getByteFrequencyData(dataArray);
      const hasAudioData = dataArray.some((value) => value > 0);

      if (hasAudioData) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const barWidth = 2;
        const gap = 5;
        let x = 0;
        const centerY = canvas.height / 2;
        dataArray.forEach((value) => {
          const barHeight = (value / 255) * (canvas.height / 2) * 0.8;
          ctx.fillStyle = TTSWaveformColor;
          ctx.fillRect(x, centerY - barHeight, barWidth, barHeight);
          ctx.fillRect(x, centerY, barWidth, barHeight);
          x += barWidth + gap;
        });
      } else {
        drawStraightLine(ctx, canvas);
      }

      rafIdRef.current = requestAnimationFrame(draw);
    };

    rafIdRef.current = requestAnimationFrame(draw);
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [analyser]);

  return (
    <canvas
      ref={canvasRef}
      width={800}
      height={100}
      style={{ width: "100%", height: "35px" }}
    />
  );
}